import {
  type Component,
  createSignal,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { useAutoConfidenceScoreExplanation } from "../api";
import type { ConfidenceScoreBreakdown } from "../types";

/**
 * Il box "Perché questo match" nello sheet del freelancer (AC15–AC21): il
 * commento che spiega il punteggio, non più nascosto in un tooltip hover.
 *
 * L'etichetta AI si riferisce al MATCH calcolato, mai al testo del commento
 * (D4: il commento è un template deterministico sul BE, l'AI vera è la
 * ricerca che calcola il punteggio).
 *
 * Il chiamante monta il box keyed sul breakdown corrente (E-16): al cambio
 * profilo il box si ricrea — mai il commento di A sotto il nome di B, stato
 * espanso resettato, cache hit → testo immediato senza skeleton.
 *
 * Il box vive nello slot `matchExplanation` di `ProfileInfo`, subito sotto la
 * barra del punteggio: monta quindi solo col profilo risolto. Per questo il
 * funnel del commento NON sta qui ma nello sheet, dove l'observer è attivo da
 * quando lo sheet si apre — altrimenti una generazione conclusa prima del
 * profilo non verrebbe mai misurata.
 *
 * Ingombro riservato (E-21/D10): `min-h` di 4 righe sull'elemento che porta
 * `text-sm leading-relaxed`, così skeleton→testo non sposta il profilo che
 * l'utente sta già leggendo. Espansione "Mostra tutto" senza animazione
 * (D11, pattern `about-section`: line-clamp non interpolabile, crescita solo
 * verso il basso, bottone solo se il testo trabocca davvero).
 */
export const ConfidenceScoreExplanationBox: Component<{
  breakdown: ConfidenceScoreBreakdown;
  /** Lo sheet sta mostrando il profilo: il fetch parte solo qui (AC17). */
  active: boolean;
}> = (props) => {
  const { t } = useI18n();
  const explanationQuery = useAutoConfidenceScoreExplanation(
    () => props.breakdown,
    () => props.active,
  );

  const [expanded, setExpanded] = createSignal(false);
  const [overflows, setOverflows] = createSignal(false);
  let textRef: HTMLParagraphElement | undefined;
  let observer: ResizeObserver | undefined;

  const measure = () => {
    if (!textRef) return;
    // Significativo solo da clampato: espanso, le due altezze coincidono.
    if (expanded()) return;
    setOverflows(textRef.scrollHeight > textRef.clientHeight + 1);
  };

  onMount(() => {
    if (typeof ResizeObserver === "undefined") return;
    observer = new ResizeObserver(measure);
    if (textRef) observer.observe(textRef);
    onCleanup(() => observer?.disconnect());
  });

  // Il testo arriva in modo asincrono: il ref si registra quando il nodo
  // esiste, qualunque sia l'ordine rispetto a onMount.
  const registerText = (el: HTMLParagraphElement) => {
    textRef = el;
    observer?.observe(el);
    queueMicrotask(measure);
  };

  return (
    <section class="rounded-lg border border-primary/25 bg-card p-4 shadow-sm">
      <div class="flex items-center gap-2">
        <i
          class="ph-fill ph-sparkle text-base text-primary"
          aria-hidden="true"
        />
        <h3 class="text-sm font-semibold text-foreground">
          {t("company.search.freelancers.result.score_explanation.box_title")}
        </h3>
      </div>

      {/* Lo slot a ingombro riservato: 4 righe di text-sm/leading-relaxed. */}
      <div class="mt-2 min-h-[calc(4*1.625em)] text-sm leading-relaxed">
        <Switch>
          <Match when={explanationQuery.isError}>
            {/* Errore contenuto nel box (AC18), fuori dalla live region:
                lo sheet resta pienamente utilizzabile. */}
            <div class="flex flex-wrap items-center gap-3">
              <p class="text-muted-foreground">
                {t("company.search.freelancers.result.score_explanation.error")}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void explanationQuery.refetch()}
              >
                {t("common.retry")}
              </Button>
            </div>
          </Match>
          <Match when={true}>
            {/* La live region esiste dal mount: l'arrivo del testo è
                annunciato in modo cortese, senza rubare il focus (AC21). */}
            <div aria-live="polite" aria-busy={explanationQuery.isFetching}>
              {/* isSuccess, mai `data !== undefined`: solid-query sospende
                  sull'accesso a `data` in pending iniziale e lo sheet vive
                  dentro la Suspense del router. */}
              <Show
                when={explanationQuery.isSuccess}
                fallback={
                  <div class="space-y-2" aria-hidden="true">
                    <div class="skeleton-shimmer h-3.5 w-full rounded" />
                    <div class="skeleton-shimmer h-3.5 w-full rounded" />
                    <div class="skeleton-shimmer h-3.5 w-[70%] rounded" />
                  </div>
                }
              >
                <p
                  ref={registerText}
                  class="search-fade-in whitespace-pre-line text-muted-foreground"
                  classList={{ "line-clamp-4": !expanded() }}
                >
                  {explanationQuery.data}
                </p>
              </Show>
            </div>
          </Match>
        </Switch>
      </div>

      <Show when={overflows() || expanded()}>
        {/* Tap area ≥44px senza ingombro extra (py-3 compensato dal -my-3). */}
        <button
          type="button"
          class="-my-3 self-start py-3 text-sm font-medium text-primary hover:underline"
          aria-expanded={expanded()}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded()
            ? t("company.search.freelancers.result.score_explanation.show_less")
            : t("company.search.freelancers.result.score_explanation.show_all")}
        </button>
      </Show>
    </section>
  );
};
